import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import NextError from 'next/error';
import Head from 'next/head';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';
import { useAuth0User } from '@hooks/useAuth0User';
import { usePlayers } from '@hooks/usePlayers';
import { usePurchaseConfirmationAnalytics } from '@hooks/usePurchaseConfirmationAnalytics';

import { WEBSITE_NAME } from '@lib/constants';
import { BodyRegular, H1 } from '@components/atoms/typography';
import { buildLoginHref } from '@lib/auth0/login-url';
import Button from '@components/atoms/button';
import PlayerFactsTypewriter from '@components/player-facts-typewriter';
import { CHECKOUT_RETURN_PATHS } from '@lib/checkout/return-paths';
import { datadogRumService } from '@lib/datadogRum';
import { awaitPaidEntitlement } from '@lib/checkout/await-paid-entitlement';
import { ACCESS_TOKEN_QUERY_KEY } from '@hooks/useAccessToken';
import { ENTITLEMENTS_QUERY_KEY } from '@hooks/useEntitlements';

const TYPEWRITER_DURATION_MS = 8000;

// The typewriter draws its facts from the most-owned players, so the names it
// shows are ones the reader recognises.
const TYPEWRITER_PLAYER_COUNT = 40;

const HUB_PATH = '/welcome';

/**
 * The /funnel-welcome redirect only applies during the Mini-League Promise
 * offer window (FFH-1597). Flip this to true when the window closes to keep
 * users on the classic thank-you layout instead: the entitlement wait still
 * runs either way, so the CTA drops them into the hub already entitled.
 */
const SHOW_LEGACY_THANK_YOU = false;

// showLegacyThankYou defaults to the module constant; getInitialProps never
// sets it, so production always takes the default. It exists purely so
// tests can force either branch without mutating module-level state.
const Thankyou = ({
  planId,
  sessionId,
  showLegacyThankYou = SHOW_LEGACY_THANK_YOU
}) => {
  const { user, isLoading } = useAuth0User();
  const router = useRouter();
  const [showMainContent, setShowMainContent] = useState(false);
  const [isEntitlementSettled, setIsEntitlementSettled] = useState(false);
  const hasStartedEntitlementWait = useRef(false);
  const queryClient = useQueryClient();
  // Telemetry values live in a ref so the wait effect's dependency array
  // stays [user, isLoading]: adding them as dependencies would let a mid-wait
  // change cancel the effect while hasStartedEntitlementWait blocks the
  // restart, stranding the page with the wait never settling.
  const entitlementTelemetryRef = useRef({ sessionId, planId });
  entitlementTelemetryRef.current = { sessionId, planId };
  // Anonymous read: the reader's entitlement claim is still being minted at
  // this point, and the facts need no personalisation. Predictions are
  // excluded to keep the payload small on a screen that lasts 8 seconds.
  const { data: playersData } = usePlayers({
    sortBy: 'ownership',
    sortDirection: 'desc',
    limit: TYPEWRITER_PLAYER_COUNT,
    exclude: 'predictions'
  });

  const typewriterPlayers = useMemo(
    () => playersData?.pages.flatMap((page) => page.data) ?? [],
    [playersData]
  );

  // The whole purchase rail (confirm, page_view, idempotent `purchase`
  // dataLayer push) lives in the shared hook. It completes well before the
  // typewriter finishes and the redirect fires.
  usePurchaseConfirmationAnalytics({ planId, sessionId });

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowMainContent(true);
    }, TYPEWRITER_DURATION_MS);

    return () => clearTimeout(timer);
  }, []);

  // FFH-1597, FFH-3019: wait for the purchase to reach the member's
  // entitlements at ffh-backend, rather than forcing Auth0 refresh-token
  // grants until its post-login Action re-minted the claim. The wait runs in
  // both layouts: it is what lets the user reach the hub or /funnel-welcome
  // already carrying their new entitlements. The retry is bounded and every
  // outcome (including no session at all) settles; the user is never
  // stranded here.
  useEffect(() => {
    if (isLoading || hasStartedEntitlementWait.current) return;
    hasStartedEntitlementWait.current = true;

    if (!user) {
      setIsEntitlementSettled(true);
      return;
    }

    let cancelled = false;
    awaitPaidEntitlement().then((outcome) => {
      if (outcome === 'entitlement-found') {
        // The wait leaves a rotated session behind it, so the cached token and
        // the cached entitlements are both a tier out of date. Dropping them
        // here means the hub renders from the new plan on arrival.
        queryClient.invalidateQueries({ queryKey: ACCESS_TOKEN_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_QUERY_KEY });
      } else {
        console.warn(`Entitlement wait settled as: ${outcome}`);
        const { sessionId: settledSessionId, planId: settledPlanId } =
          entitlementTelemetryRef.current;
        // Only a checkout return (sessionId present) is a real failure: a
        // paid user proceeding without their entitlement. RUM does not
        // collect console.warn, so report explicitly or nothing ever will.
        if (settledSessionId) {
          datadogRumService.addError(
            new Error(`Entitlement wait settled as: ${outcome}`),
            {
              context: 'post-checkout-entitlement',
              outcome,
              sessionId: settledSessionId,
              planId: settledPlanId
            }
          );
        }
      }
      if (!cancelled) setIsEntitlementSettled(true);
    });

    return () => {
      cancelled = true;
    };
  }, [user, isLoading, queryClient]);

  // During the offer window every checkout return moves on to the
  // mini-league capture on /funnel-welcome once the wait has settled.
  // Gated on sessionId so an invalid direct visit falls through to the
  // <Error statusCode={500} /> render below instead of being redirected
  // away from it.
  useEffect(() => {
    if (showLegacyThankYou || !sessionId) return;
    if (showMainContent && isEntitlementSettled) {
      router.push(CHECKOUT_RETURN_PATHS.funnelWelcome);
    }
  }, [
    showLegacyThankYou,
    showMainContent,
    isEntitlementSettled,
    sessionId,
    router
  ]);

  const handleHubClick = () => {
    if (user) {
      router.push(HUB_PATH);
      return;
    }

    router.push(buildLoginHref(HUB_PATH));
  };

  if (!showMainContent) {
    return (
      <div>
        <Head>
          <title>{`Welcome | ${WEBSITE_NAME}`}</title>
        </Head>
        <main className="bg-gray-50 flex items-center justify-center">
          <PlayerFactsTypewriter players={typewriterPlayers} />
        </main>
      </div>
    );
  }

  if (!sessionId) {
    return <NextError statusCode={500} />;
  }

  if (showLegacyThankYou) {
    return (
      <div>
        <Head>
          <title>{`Welcome | ${WEBSITE_NAME}`}</title>
        </Head>
        <main>
          <div className="bg-white flex flex-col justify-center py-[50px] sm:px-6 lg:px-8">
            <div className="flex justify-center mb-5 px-5">
              <Image
                src="/images/football-boots.png"
                height={250}
                width={500}
                priority
                alt="Football boots on a football pitch"
              />
            </div>

            <div className="flex flex-col items-center justify-center px-3">
              <H1 className="text-center">
                Welcome to Fantasy Football Hub <span className="ml-1">🎉</span>
              </H1>
              <BodyRegular>Thanks for being one of our subscribers</BodyRegular>
            </div>

            <div className="flex justify-center items-center mt-5">
              <Button
                variant="primary"
                onClick={handleHubClick}
                disabled={!isEntitlementSettled}
              >
                {!isEntitlementSettled
                  ? 'Setting up your account…'
                  : user
                  ? 'Explore the hub'
                  : 'Click here to log in'}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Shown only while the entitlement wait outlasts the typewriter (a slow
  // Stripe webhook). The redirect waits for BOTH the typewriter minimum and
  // the wait to settle; by the time this renders the typewriter gate has
  // already passed, so it leaves the moment the wait settles.
  return (
    <div>
      <Head>
        <title>{`Welcome | ${WEBSITE_NAME}`}</title>
      </Head>
      <main>
        <div className="bg-white flex flex-col items-center justify-center py-[50px] px-3 sm:px-6 lg:px-8">
          <H1 className="text-center">Setting up your account…</H1>
          <BodyRegular className="mt-2 animate-pulse text-center">
            This only takes a few seconds.
          </BodyRegular>
        </div>
      </main>
    </div>
  );
};

Thankyou.getInitialProps = async (ctx) => {
  const sessionId = ctx.query.subscription_id ?? ctx.query.session;
  const { plan_id: planId } = ctx.query;

  return {
    planId,
    sessionId
  };
};

export default Thankyou;
